import { useSession } from "@outschool/ui-auth";
import gql from "graphql-tag";
import _ from "lodash";
import { useCallback } from "react";
import { useMutation } from "react-apollo";

import { validOptimisticResponse } from "../../shared/client/ApolloClient";
import {
  AddChildMutation,
  AddChildMutationVariables,
  ChildFragmentFragment,
  CurrentUserQueryQuery,
  DeleteChildMutation,
  DeleteChildMutationVariables,
  UpdateChildMutation,
  UpdateChildMutationVariables
} from "../../shared/generated/graphql";
import { CurrentUserChildFragment } from "../queries/ChildFragment";
import { currentUserQuery } from "../queries/CurrentUserQuery";

export function useCurrentUserLearners(): Array<ChildFragmentFragment> {
  const { currentUser } = useSession();
  return currentUser?.children ?? [];
}

const addChildMutation = gql`
  mutation AddChild($childInput: ChildInput!) {
    addChild(childInput: $childInput) {
      ...CurrentUserChildFragment
    }
  }
  ${CurrentUserChildFragment}
`;
export function useAddChildMutation() {
  const [addChildMutate] = useMutation<
    AddChildMutation,
    AddChildMutationVariables
  >(addChildMutation);

  return useCallback(
    child =>
      addChildMutate({
        variables: { childInput: child },
        update: (store, { data: { addChild } }) => {
          const data = store.readQuery<CurrentUserQueryQuery>({
            query: currentUserQuery
          });
          store.writeQuery({
            query: currentUserQuery,
            data: {
              currentUser: {
                ...data.currentUser,
                children: [...data.currentUser.children, addChild]
              }
            }
          });
        }
      }),
    [addChildMutate]
  );
}

const updateChildMutation = gql`
  mutation UpdateChild($uid: ID!, $childInput: ChildInput!) {
    updateChild(uid: $uid, childInput: $childInput) {
      ...CurrentUserChildFragment
    }
  }
  ${CurrentUserChildFragment}
`;
export function useUpdateChildMutation() {
  const [updateChildMutate] = useMutation<
    UpdateChildMutation,
    UpdateChildMutationVariables
  >(updateChildMutation);

  return useCallback(
    child => {
      const keysToInclude = [
        "name",
        "email",
        "avatar",
        "pronoun",
        "intro",
        "birthMonth",
        "birthYear",
        "optedOutOfPersonalizedRecommendationsAt",
        "isLoginEnabled",
        "username",
        "password"
      ];
      // Only pass age when date of birth is empty
      if (!(child.birthMonth && child.birthYear)) {
        keysToInclude.push("age");
      }
      return updateChildMutate({
        variables: { childInput: _.pick(child, keysToInclude), uid: child.uid },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          updateChild: {
            ...child
          }
        })
      });
    },
    [updateChildMutate]
  );
}

export const deleteChildMutation = gql`
  mutation deleteChild($uid: ID!) {
    deleteChild(uid: $uid)
  }
`;

export function useDeleteChildMutation() {
  const [deleteChildMutate] = useMutation<
    DeleteChildMutation,
    DeleteChildMutationVariables
  >(deleteChildMutation);

  return useCallback(
    childUid =>
      deleteChildMutate({
        variables: { uid: childUid },
        refetchQueries: ["UserAdmin"],
        update: (store, { data: { deleteChild } }) => {
          if (!deleteChild) {
            return;
          }
          const data = store.readQuery<CurrentUserQueryQuery>({
            query: currentUserQuery
          });
          data.currentUser.children = data.currentUser.children.filter(
            child => child.uid !== childUid
          );
          store.writeQuery({ query: currentUserQuery, data });
        }
      }),
    [deleteChildMutate]
  );
}
